/**
 * Worker 请求用途。
 *
 * 设计说明：
 * 不同用途的 Worker 请求对过期响应的容忍度不同。几何、特征和历史回放结果会写回 CAD 主状态，
 * 必须严格绑定发起请求时的模型 revision；Worker 生命周期请求只负责建立 Worker 可用性，不写回模型。
 */
export type WorkerRevisionPurpose =
    | 'worker-lifecycle'
    | 'feature-preview'
    | 'feature-rebuild'
    | 'mesh-build'
    | 'custom';

/**
 * Worker revision token。
 *
 * token 在发起异步 Worker 请求时创建，并随请求发送。Worker 结果返回后必须用当前文档 revision /
 * historySeq 校验 token，过期结果不得写回主状态。
 */
export interface WorkerRevisionToken {
    /** 所属文档 ID。 */
    readonly documentId: string;
    /** 请求 ID。 */
    readonly requestId: string;
    /** 请求序号。 */
    readonly requestSeq: number;
    /** 发起请求时的文档 revision。 */
    readonly documentRevision: number;
    /** 发起请求时的历史序号。 */
    readonly historySeq: number;
    /** 请求用途。 */
    readonly purpose: WorkerRevisionPurpose;
    /** 创建时间戳。 */
    readonly createdAt: number;
}

/**
 * Worker revision guard 状态。
 */
export interface WorkerRevisionGuardState {
    /** 当前文档 ID。 */
    readonly documentId: string;
    /** 当前文档 revision。 */
    readonly documentRevision: number;
    /** 当前历史序号。 */
    readonly historySeq: number;
    /** 最新请求序号。 */
    readonly latestRequestSeq: number;
}

/**
 * Worker revision guard 配置。
 */
export interface WorkerRevisionGuardOptions {
    /** 文档 ID。 */
    readonly documentId: string;
    /** 初始文档 revision。 */
    readonly initialDocumentRevision?: number;
    /** 初始历史序号。 */
    readonly initialHistorySeq?: number;
    /** 当前时间函数；测试时可注入稳定时间。 */
    readonly now?: () => number;
    /** 是否只接受最新请求；同一 revision 下的独立并行任务应保持为 false。 */
    readonly retirePreviousRequests?: boolean;
}

/**
 * 创建 Worker token 输入。
 */
export interface CreateWorkerRevisionTokenInput {
    /** 请求用途。 */
    readonly purpose: WorkerRevisionPurpose;
    /** 可选请求 ID；不传则由 guard 生成。 */
    readonly requestId?: string;
}

/**
 * Worker revision 接受决策原因。
 */
export type WorkerRevisionDecisionReason =
    | 'accepted'
    | 'document-mismatch'
    | 'document-revision-stale'
    | 'history-seq-stale'
    | 'request-retired';

/**
 * Worker revision 接受决策。
 */
export interface WorkerRevisionDecision {
    /** 是否接受 Worker 结果。 */
    readonly accepted: boolean;
    /** 决策原因。 */
    readonly reason: WorkerRevisionDecisionReason;
    /** 被校验的 token。 */
    readonly token: WorkerRevisionToken;
    /** 校验时的 guard 状态。 */
    readonly state: WorkerRevisionGuardState;
}

/**
 * Worker revision guard。
 */
export interface WorkerRevisionGuard {
    /**
     * 创建异步 Worker 请求 token。
     *
     * @param input token 输入。
     * @returns Worker revision token。
     */
    readonly createToken: (input: CreateWorkerRevisionTokenInput) => WorkerRevisionToken;
    /**
     * 推进文档 revision。
     *
     * @param documentRevision 新文档 revision。
     * @returns guard 状态。
     */
    readonly advanceDocumentRevision: (documentRevision: number) => WorkerRevisionGuardState;
    /**
     * 推进历史序号。
     *
     * @param historySeq 新历史序号。
     * @returns guard 状态。
     */
    readonly advanceHistorySeq: (historySeq: number) => WorkerRevisionGuardState;
    /**
     * 校验 Worker 结果是否仍可写回。
     *
     * @param token Worker revision token。
     * @returns 接受决策。
     */
    readonly shouldAccept: (token: WorkerRevisionToken) => WorkerRevisionDecision;
    /**
     * 读取 guard 状态。
     *
     * @returns guard 状态。
     */
    readonly getState: () => WorkerRevisionGuardState;
}

/**
 * 创建 guard 状态快照。
 *
 * @param documentId 文档 ID。
 * @param documentRevision 文档 revision。
 * @param historySeq 历史序号。
 * @param latestRequestSeq 最新请求序号。
 * @returns guard 状态。
 */
function createWorkerRevisionGuardState(
    documentId: string,
    documentRevision: number,
    historySeq: number,
    latestRequestSeq: number,
): WorkerRevisionGuardState {
    return {
        documentId,
        documentRevision,
        historySeq,
        latestRequestSeq,
    };
}

/**
 * 创建接受决策。
 *
 * @param accepted 是否接受。
 * @param reason 决策原因。
 * @param token Worker token。
 * @param state guard 状态。
 * @returns 接受决策。
 */
function createWorkerRevisionDecision(
    accepted: boolean,
    reason: WorkerRevisionDecisionReason,
    token: WorkerRevisionToken,
    state: WorkerRevisionGuardState,
): WorkerRevisionDecision {
    return {
        accepted,
        reason,
        token,
        state,
    };
}

/**
 * 判断 token 是否只描述 Worker 生命周期。
 *
 * 设计说明：
 * Worker 初始化只建立 Worker 与 WASM 的可用性，不会把几何结果、特征结果或历史回放结果写回 CAD 主状态。
 * 因此它必须仍然绑定当前文档 ID，但不应被模型 documentRevision/historySeq 推进误判为过期。
 *
 * @param purpose Worker 请求用途。
 * @returns 是否为 Worker 生命周期请求。
 */
function isWorkerLifecyclePurpose(purpose: WorkerRevisionPurpose): boolean {
    return purpose === 'worker-lifecycle';
}

/**
 * 创建 Worker revision guard。
 *
 * @param options guard 配置。
 * @returns Worker revision guard。
 */
export function createWorkerRevisionGuard(
    options: WorkerRevisionGuardOptions,
): WorkerRevisionGuard {
    let documentRevision = options.initialDocumentRevision ?? 0;
    let historySeq = options.initialHistorySeq ?? 0;
    let latestRequestSeq = 0;

    const getState = () => createWorkerRevisionGuardState(
        options.documentId,
        documentRevision,
        historySeq,
        latestRequestSeq,
    );

    return {
        createToken(input) {
            latestRequestSeq += 1;

            return {
                documentId: options.documentId,
                requestId: input.requestId ?? `worker-request-${latestRequestSeq}`,
                requestSeq: latestRequestSeq,
                documentRevision,
                historySeq,
                purpose: input.purpose,
                createdAt: options.now?.() ?? Date.now(),
            };
        },
        advanceDocumentRevision(nextDocumentRevision) {
            if (nextDocumentRevision < documentRevision) {
                throw new Error(`文档 revision 不能回退：${documentRevision} -> ${nextDocumentRevision}`);
            }

            documentRevision = nextDocumentRevision;

            return getState();
        },
        advanceHistorySeq(nextHistorySeq) {
            if (nextHistorySeq < historySeq) {
                throw new Error(`历史序号不能回退：${historySeq} -> ${nextHistorySeq}`);
            }

            historySeq = nextHistorySeq;

            return getState();
        },
        shouldAccept(token) {
            const state = getState();
            const isLifecycle = isWorkerLifecyclePurpose(token.purpose);

            if (token.documentId !== state.documentId) {
                return createWorkerRevisionDecision(false, 'document-mismatch', token, state);
            }

            if (!isLifecycle && token.documentRevision !== state.documentRevision) {
                return createWorkerRevisionDecision(false, 'document-revision-stale', token, state);
            }

            if (!isLifecycle && token.historySeq !== state.historySeq) {
                return createWorkerRevisionDecision(false, 'history-seq-stale', token, state);
            }

            if (options.retirePreviousRequests && token.requestSeq < state.latestRequestSeq) {
                return createWorkerRevisionDecision(false, 'request-retired', token, state);
            }

            return createWorkerRevisionDecision(true, 'accepted', token, state);
        },
        getState,
    };
}
